import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

const stepsData = {
  en: [
    { number: '01', icon: 'ri-search-eye-line', label: 'DISCOVERY', title: 'Understand Your Needs', bullets: ['In-depth needs analysis', 'AI + Expert review', 'Curated vehicle matches'], accentLight: '#e8d8c0', accentDim: 'rgba(232,216,192,0.15)' },
    { number: '02', icon: 'ri-checkbox-circle-line', label: 'DECISION', title: 'Pick the Right One', bullets: ['Review your shortlist', 'Compare side-by-side', 'Choose with confidence'], accentLight: '#c4a882', accentDim: 'rgba(196,168,130,0.15)' },
    { number: '03', icon: 'ri-shake-hands-line', label: 'WE HANDLE IT', title: 'Source, Negotiate & Manage', bullets: ['We locate your exact vehicle', 'Negotiate the best deal', 'Save time & dealer visits'], accentLight: '#a88c6a', accentDim: 'rgba(168,140,106,0.15)' },
    { number: '04', icon: 'ri-steering-line', label: 'DRIVE AWAY', title: 'Drive Away & Enjoy', bullets: ['Delivered to your door', 'Zero stress, zero regrets', 'Pure driving pleasure'], accentLight: '#8a7560', accentDim: 'rgba(138,117,96,0.15)' },
  ],
  fr: [
    { number: '01', icon: 'ri-search-eye-line', label: 'DÉCOUVERTE', title: 'Comprendre Vos Besoins', bullets: ['Analyse approfondie de vos besoins', 'Revue IA + Expert', 'Correspondances de véhicules'], accentLight: '#e8d8c0', accentDim: 'rgba(232,216,192,0.15)' },
    { number: '02', icon: 'ri-checkbox-circle-line', label: 'DÉCISION', title: 'Choisissez le Bon', bullets: ['Passez en revue votre sélection', 'Comparez côte à côte', 'Choisissez en toute confiance'], accentLight: '#c4a882', accentDim: 'rgba(196,168,130,0.15)' },
    { number: '03', icon: 'ri-shake-hands-line', label: "ON S'EN OCCUPE", title: 'Trouver, Négocier & Gérer', bullets: ['Nous localisons votre véhicule', 'Négocions le meilleur prix', 'Économisez temps et visites'], accentLight: '#a88c6a', accentDim: 'rgba(168,140,106,0.15)' },
    { number: '04', icon: 'ri-steering-line', label: 'PROFITEZ', title: 'Conduisez & Profitez', bullets: ['Livré à votre porte', 'Zéro stress, zéro regret', 'Plaisir de conduite pur'], accentLight: '#8a7560', accentDim: 'rgba(138,117,96,0.15)' },
  ],
};

const content = {
  en: {
    headline: 'Your Path to the Perfect Vehicle',
    sub: 'Four steps. Zero stress. Total confidence.',
    note: 'We hope to save you money — we guarantee to save you time.',
  },
  fr: {
    headline: 'Votre Chemin vers le Véhicule Parfait',
    sub: 'Quatre étapes. Zéro stress. Confiance totale.',
    note: "Nous espérons vous faire économiser de l'argent — nous garantissons de vous faire économiser du temps.",
  },
};

interface ShootingDotProps {
  active: boolean;
  delay: number;
  color: string;
  dimColor: string;
  isLast: boolean;
  isMobile: boolean;
}

function ShootingDot({ active, delay, color, dimColor, isLast, isMobile }: ShootingDotProps) {
  return (
    <div className="flex items-center mb-4 sm:mb-5 relative z-10 h-9">
      <div className="relative mx-auto flex items-center justify-center" style={{ width: '36px', height: '36px' }}>
        {/* Comet trail — from left on desktop, from top on mobile */}
        {isMobile ? (
          <div
            className="absolute left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
            style={{
              bottom: 'calc(50% + 5px)',
              width: '2px',
              height: active ? '48px' : '0px',
              background: `linear-gradient(to bottom, transparent, ${color})`,
              opacity: active ? 0.65 : 0,
              transition: `height 0.28s cubic-bezier(.22,1,.36,1) ${delay + 0.08}s, opacity 0.28s ease ${delay + 0.08}s`,
            }}
          />
        ) : (
          <div
            className="absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none"
            style={{
              right: 'calc(50% + 5px)',
              height: '2px',
              width: active ? '48px' : '0px',
              background: `linear-gradient(to right, transparent, ${color})`,
              opacity: active ? 0.65 : 0,
              transition: `width 0.28s cubic-bezier(.22,1,.36,1) ${delay + 0.08}s, opacity 0.28s ease ${delay + 0.08}s`,
            }}
          />
        )}

        {/* Outer pulse ring */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: '30px',
            height: '30px',
            border: `1px solid ${color}`,
            opacity: active ? 0.18 : 0,
            transform: active ? 'scale(1)' : 'scale(0.2)',
            transition: `opacity 0.4s ease ${delay + 0.55}s, transform 0.4s cubic-bezier(.22,1,.36,1) ${delay + 0.55}s`,
            animation: active ? `dot-ring-expand 2.4s ease-out ${delay + 1}s infinite` : 'none',
          }}
        />

        {/* Mid glow ring */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: '18px',
            height: '18px',
            background: dimColor,
            opacity: active ? 1 : 0,
            transform: active ? 'scale(1)' : 'scale(0)',
            transition: `opacity 0.3s ease ${delay + 0.5}s, transform 0.35s cubic-bezier(.22,1,.36,1) ${delay + 0.5}s`,
          }}
        />

        {/* Core dot — shoots from left (desktop) or from top (mobile) */}
        <div
          className="relative z-10 rounded-full"
          style={{
            width: '9px',
            height: '9px',
            background: color,
            boxShadow: `0 0 10px 3px ${color}55, 0 0 20px 6px ${color}22`,
            opacity: active ? 1 : 0,
            transform: active
              ? 'translate(0,0) scale(1)'
              : isMobile
                ? 'translateY(-52px) scale(0.3)'
                : 'translateX(-52px) scale(0.3)',
            transition: `opacity 0.22s ease ${delay + 0.1}s, transform 0.42s cubic-bezier(.22,1,.36,1) ${delay + 0.1}s`,
          }}
        />

        {/* Tail after dot — horizontal on desktop, vertical on mobile */}
        {!isLast && (
          isMobile ? (
            <div
              className="absolute left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
              style={{
                top: 'calc(50% + 5px)',
                width: '1px',
                height: active ? '36px' : '0px',
                background: `linear-gradient(to bottom, ${color}50, transparent)`,
                transition: `height 0.3s ease ${delay + 0.35}s`,
              }}
            />
          ) : (
            <div
              className="hidden lg:block absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none"
              style={{
                left: 'calc(50% + 5px)',
                height: '1px',
                width: active ? '36px' : '0px',
                background: `linear-gradient(to right, ${color}50, transparent)`,
                transition: `width 0.3s ease ${delay + 0.35}s`,
              }}
            />
          )
        )}
      </div>
    </div>
  );
}

function LiftCard({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className ?? ''}
      style={{
        ...style,
        transform: hovered ? 'translateY(-6px) scale(1.015)' : 'translateY(0) scale(1)',
        transition: hovered ? 'transform 0.18s cubic-bezier(.22,1,.36,1)' : 'transform 0.40s cubic-bezier(.22,1,.36,1)',
        willChange: 'transform',
        position: 'relative',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ borderRadius: 'inherit', background: 'rgba(196,168,130,0.08)', opacity: hovered ? 1 : 0, transition: 'opacity 0.18s ease' }} />
      {children}
    </div>
  );
}

export default function HowItWorksSection() {
  const { lang } = useLanguage();
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Reset on lang change so cards re-animate
  useEffect(() => {
    setVisibleCards([]);
  }, [lang]);

  // Per-card observers — each card fires when it enters the viewport
  useEffect(() => {
    const observers = cardRefs.current.map((ref, i) => {
      if (!ref) return null;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleCards((prev) => [...new Set([...prev, i])]);
              obs.disconnect();
            }
          });
        },
        { threshold: 0.12 }
      );
      obs.observe(ref);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, [lang]);

  const steps = stepsData[lang];
  const t = content[lang];

  return (
    <section id="how-it-works" className="relative pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-24 overflow-hidden" style={{ background: '#0c0c16' }}>
      <style>{`
        @keyframes dot-ring-expand {
          0%   { transform: scale(1);   opacity: 0.18; }
          60%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>

      {/* BG texture */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(196,168,130,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(196,168,130,0.035) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(196,168,130,0.03) 0%, transparent 70%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(196,168,130,0.15) 30%, rgba(196,168,130,0.3) 50%, rgba(196,168,130,0.15) 70%, transparent 100%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
        {/* Section Header */}
        <div className="mb-12 sm:mb-16 lg:mb-20 text-center">
          <h2 className="text-[26px] sm:text-4xl lg:text-6xl font-black leading-tight mb-3 sm:mb-5 text-white" style={{ fontFamily: 'Satoshi, Inter, sans-serif', letterSpacing: '-0.02em' }}>
            {t.headline}
          </h2>
          <p className="text-sm sm:text-base" style={{ color: 'rgba(196,168,130,0.6)', fontFamily: 'Inter, sans-serif' }}>{t.sub}</p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 relative">
          {/* Thin static connector line behind all dots (desktop) */}
          <div className="hidden lg:block absolute top-[18px] left-[12.5%] right-[12.5%] h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(196,168,130,0.1) 10%, rgba(196,168,130,0.1) 90%, transparent 100%)' }} />

          {steps.map((step, index) => {
            const visible = visibleCards.includes(index);
            return (
              <div
                key={`${lang}-${index}`}
                ref={(el) => { cardRefs.current[index] = el; }}
                className="relative flex flex-col"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0px)' : 'translateY(40px)',
                  transition: 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)',
                }}
              >
                {/* Shooting dot — replaces number circle */}
                <ShootingDot
                  active={visible}
                  delay={0}
                  color={step.accentLight}
                  dimColor={step.accentDim}
                  isLast={index === steps.length - 1}
                  isMobile={isMobile}
                />

                <LiftCard
                  className="flex-1 rounded-2xl p-5 sm:p-6 relative overflow-hidden cursor-default"
                  style={{ background: 'rgba(196,168,130,0.04)', border: '1px solid rgba(196,168,130,0.12)' }}
                >
                  {/* Decorative number watermark */}
                  <div
                    className="absolute -bottom-2 -right-1 text-[80px] sm:text-[90px] font-black leading-none pointer-events-none select-none"
                    style={{ color: step.accentLight, opacity: 0.04, fontFamily: 'Satoshi, Inter, sans-serif' }}
                  >
                    {step.number}
                  </div>

                  {/* Top accent bar */}
                  <div className="w-8 h-0.5 rounded-full mb-5" style={{ background: `linear-gradient(90deg, ${step.accentLight}, transparent)` }} />

                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: step.accentDim, border: `1px solid ${step.accentLight}22` }}>
                    <i className={`${step.icon} text-xl`} style={{ color: step.accentLight }} />
                  </div>

                  {/* Label */}
                  <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.18em] uppercase mb-1.5 block" style={{ color: 'rgba(196,168,130,0.5)', fontFamily: 'Inter, sans-serif' }}>
                    {step.label}
                  </span>

                  {/* Title */}
                  <h3 className="text-base sm:text-lg font-bold mb-4 leading-snug text-white" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>
                    {step.title}
                  </h3>

                  {/* Bullets */}
                  <ul className="space-y-2">
                    {step.bullets.map((bullet, bi) => (
                      <li key={bi} className="flex items-start gap-2.5">
                        <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: step.accentLight, opacity: 0.7 }} />
                        <span className="text-xs sm:text-sm leading-snug" style={{ color: 'rgba(196,168,130,0.55)', fontFamily: 'Inter, sans-serif' }}>{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Arrow connector between cards (desktop) */}
                  {index < steps.length - 1 && (
                    <div
                      className="hidden lg:flex absolute -right-[14px] top-1/2 -translate-y-1/2 w-7 h-7 rounded-full items-center justify-center z-20"
                      style={{ background: '#0c0c16', border: '1px solid rgba(196,168,130,0.15)' }}
                    >
                      <i className="ri-arrow-right-s-line text-base" style={{ color: 'rgba(196,168,130,0.4)' }} />
                    </div>
                  )}
                </LiftCard>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div
          className="mt-12 sm:mt-16 text-center"
          style={{
            opacity: visibleCards.length === steps.length ? 1 : 0,
            transform: visibleCards.length === steps.length ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.5s ease-out 0.1s, transform 0.5s ease-out 0.1s',
          }}
        >
          <p className="inline-flex items-center gap-2 text-xs sm:text-sm px-5 py-2.5 rounded-full" style={{ background: 'rgba(196,168,130,0.06)', border: '1px solid rgba(196,168,130,0.12)', color: 'rgba(196,168,130,0.5)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
            <i className="ri-information-line text-[#c4a882] opacity-50" />
            {t.note}
          </p>
        </div>
      </div>
    </section>
  );
}
